import type { RpcTransport, RpcRequest, RpcResponse, RpcClient } from "./types";

export interface RpcClientOptions {
  /**
   * Timeout for RPC requests in milliseconds.
   * Default: 30000 (30 seconds)
   * Set to 0 to disable timeout.
   */
  requestTimeoutMs?: number;
}

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timer?: ReturnType<typeof setTimeout>;
}

/**
 * Create a typed RPC client over a transport.
 * Returns a Proxy that allows calling api.method(params) which returns a Promise.
 *
 * @param transport - The transport to send requests over
 * @param options - Optional configuration
 * @returns A typed proxy object where each method call becomes an RPC request
 *
 * @example
 * ```typescript
 * interface MyApi {
 *   getUser(params: { id: string }): Promise<{ id: string; name: string }>;
 * }
 *
 * const rpc = createRpcClient<MyApi>(transport);
 * const user = await rpc.getUser({ id: "123" });
 * // TypeScript knows: user is { id: string; name: string }
 * ```
 */
export function createRpcClient<Api>(
  transport: RpcTransport,
  options: RpcClientOptions = {}
): RpcClient<Api> {
  const { requestTimeoutMs = 30000 } = options;

  let nextId = 1;
  const pending = new Map<string, PendingRequest>();

  // Listen for responses
  transport.onMessage((raw) => {
    // Ignore non-RPC messages
    if (!raw || typeof raw !== "object") return;
    const msg = raw as RpcResponse;
    if (msg.type !== "rpc:res") return;

    const entry = pending.get(msg.id);
    if (!entry) return;

    pending.delete(msg.id);
    if (entry.timer) clearTimeout(entry.timer);

    if (msg.error) {
      const err = new Error(msg.error.message);
      err.name = msg.error.name ?? "Error";
      entry.reject(err);
    } else {
      entry.resolve(msg.result);
    }
  });

  function call(method: string, params: unknown): Promise<unknown> {
    const id = String(nextId++);

    return new Promise((resolve, reject) => {
      const record: PendingRequest = { resolve, reject };

      if (requestTimeoutMs > 0) {
        record.timer = setTimeout(() => {
          pending.delete(id);
          reject(new Error(`RPC timeout for method "${method}"`));
        }, requestTimeoutMs);
      }

      pending.set(id, record);

      const req: RpcRequest = {
        type: "rpc:req",
        id,
        method,
        params,
      };
      transport.send(req);
    });
  }

  // Use Proxy so rpc.methodName(params) works
  const handler: ProxyHandler<object> = {
    get(_target, propKey) {
      if (typeof propKey !== "string") return undefined;
      return (params: unknown) => call(propKey, params);
    },
  };

  return new Proxy({}, handler) as RpcClient<Api>;
}
